import React from 'react'
import * as Yup from 'yup'
import {KTIcon} from '@/_metronic/helpers'
import {Supplier, initialSupplier} from '../../core/_models'
import {createSupplier, updateSupplier} from '../../core/_requests'
import {Formik, Field, Form, FieldArray} from 'formik'
import {
  InputText,
  Checkbox,
  FormActions,
  CustomSelect,
  handleErrors
} from '@/components/form'
import {useListView} from '@/components/table/core/ListViewProvider'
import {useQueryResponse} from '@/components/table/core/QueryResponseProvider'

const apiAuths = {
  oauth: 'TYPE_OAUTH',
  keys: 'TYPE_API_KEY',
}

const credentialsSchema = () => {
  return Yup.object().shape({
    type: Yup.string().required('Type is required'),
      email: Yup.string()
      .when('type', {
        is: apiAuths.oauth,
        then: () => Yup.string().required('Login is required')
      }),
    password: Yup.string()
      .when('type', {
        is: apiAuths.oauth,
        then: () => Yup.string().required('Password is required')
      }),
    apiKey: Yup.string()
      .when('type', {
        is: apiAuths.keys,
        then: () => Yup.string().required('API Key is required')
      }),
    secretKey: Yup.string()
      .when('type', {
        is: apiAuths.keys,
        then: () => Yup.string()
      })
  })
}
const schema = Yup.object({
  name: Yup.string().min(3, 'Minimum 3 characters').required('Name is required'),
  slug: Yup.string().min(3, 'Minimum 3 characters').required('SLUG is required'),
  country: Yup.string().length(2, '2 characters').required('Country is required'),
  currency: Yup.string().length(3, '3 characters').required('Currency is required'),
  priceDigits:  Yup.number()
    .typeError('Expects to be a number')
    .integer('Expects to be an integer')
    .min(2, 'Min 2')
    .max(4, 'Max 4')
    .required('is required'),
   contacts: Yup.array()
    .of(Yup.string().email().required())
    .notRequired(),
  apiAvailable: Yup.boolean(),
  credentials: Yup.object().when('apiAvailable', {
    is: true,
    then: credentialsSchema
  })
})

const SupplierForm = () => {
  const {itemForUpdate, setItemForUpdate, setAction} = useListView()

  const {refetch} = useQueryResponse()
  const supplier = itemForUpdate as Supplier
  const close = () => {
    setAction(undefined)
    setItemForUpdate(undefined)
  }

  return (
    <Formik
      initialValues={supplier || initialSupplier}
      validationSchema={schema}
      onSubmit={async (values, {setErrors}) => {
        try {
          if (!supplier) {
            await createSupplier(values)
          } else {
            await updateSupplier(values)
          }
          close()
          refetch()
        } catch (e: unknown) {
          handleErrors(e, setErrors)
        }
      }}
    >
      {({values}) => (
        <Form id="product-availability" className='form'>
          <div className={'row'}>
            <div className={'col-6'}>
              <Field
                name="name"
                type="text"
                component={InputText}
                label="Supplier name"
                description="Supplier brand name"
                required={true}
              />
            </div>
            <div className={'col-6'}>
              <Field
                name="slug"
                type="text"
                component={InputText}
                label="Supplier slug"
                description="Expected internal identifier"
                required={true}
              />
            </div>
          </div>

          <div className={'row'}>
            <div className={'col-4'}>
              <Field
                name="country"
                maxLength="2"
                type="text"
                component={InputText}
                label="Country"
                description="Country code"
                required={true}
              />
            </div>

            <div className={'col-4'}>
              <Field
                name="currency"
                minLength="3"
                maxLength="3"
                type="text"
                component={InputText}
                label="Currency"
                description="Paid currency"
                required={true}
              />
            </div>

            <div className={'col-4'}>
              <Field
                name="priceDigits"
                min="2"
                max="4"
                type="number"
                component={InputText}
                label="Price float"
                description="Number of digits"
                required={true}
              />
            </div>


          </div>

          <Field
            name="status"
            component={CustomSelect}
            label="Status"
            options={[{
              value: 'STATUS_ENABLED', label: 'Enabled',
            } ,{
              value: 'STATUS_DISABLED', label: 'Disabled',
            } ,{
              value: 'STATUS_ARCHIVED', label: 'Archived',
            }]}
          />


          <div className={'row'}>
            <div className={'col-6'}>
              <Field
                id='apiAvailable'
                name="apiAvailable"
                component={Checkbox}
                label="API available"
                defaultChecked={supplier?.apiAvailable}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  // Reset credentials values if not checked
                  if (e.target.checked === false) {
                    values.credentials = undefined
                  }
                }}
              />
            </div>
          </div>

          {values.apiAvailable === true && (
            <>
              <div className='separator separator-dashed my-5'></div> 
              <h2 className="text-center text-dark">API credentials</h2>
              <div className='row mt-5'>
                <div className='col-lg-6'>
                  <Field
                    type='radio'
                    className='btn-check'
                    name='credentials.type'
                    value={apiAuths.oauth}
                    id='credential_type_oauth'
                  />
                  <label
                    className='btn btn-outline btn-outline-dashed btn-outline-default p-7 d-flex align-items-center mb-10'
                    htmlFor='credential_type_oauth'
                  >
                    <KTIcon iconName='lock-2' className='fs-3x me-5' />
                    <span className='d-block fw-bold text-start'>
                      <span className='text-dark fw-bolder d-block fs-4 mb-2'>OAUTH</span>
                    </span>
                  </label>
                </div>

                <div className='col-lg-6'>
                  <Field
                    type='radio'
                    className='btn-check'
                    name='credentials.type'
                    value={apiAuths.keys}
                    id='credential_type_api_key'
                  />
                  <label
                    className='btn btn-outline btn-outline-dashed btn-outline-default p-7 d-flex align-items-center'
                    htmlFor='credential_type_api_key'
                  >
                    <KTIcon iconName='key' className='fs-3x me-5' />
                    <span className='d-block fw-bold text-start'>
                      <span className='text-dark fw-bolder d-block fs-4 mb-2'>API Key</span>
                    </span>
                  </label>
                </div>
              </div>
              {values?.credentials?.type === apiAuths.oauth && (
                <div className='row'>
                  <div className='col-lg-6'>
                    <Field
                      name="credentials.email"
                      type="text"
                      component={InputText}
                      label="Login / Email"
                      required={true}
                    />
                  </div>
                  <div className='col-lg-6'>
                    <Field
                      name="credentials.password"
                      type="text"
                      component={InputText}
                      label="Password"
                      required={true}
                    />
                  </div>
                </div>
              )}

              {values?.credentials?.type === apiAuths.keys && (
                <div className='row'>
                  <div className='col-lg-6'>
                    <Field
                      name="credentials.apiKey"
                      type="text"
                      component={InputText}
                      label="API Key"
                      required={true}
                    />
                  </div>
                  <div className='col-lg-6'>
                    <Field
                      name="credentials.secretKey"
                      type="text"
                      component={InputText}
                      label="Secret Key"
                    />
                  </div>
                </div>
              )}
            </>
          )}
          <div className='separator separator-dashed my-5'></div> 
          <FieldArray name="contacts">
            {({ push, remove }) => (
              <div className="mt-10">
                <div className="d-flex justify-content-between align-items-center mb-5">
                  <h3>Contacts</h3>
                  <button
                    type="button"
                    className="btn btn-primary btn-sm d-flex align-items-center"
                    onClick={() => push('')}
                  >
                    <KTIcon iconName="plus" className="fs-3" />
                  </button>
                </div>
                
                {values.contacts && values.contacts.map((_, index) => (
                  <div key={index} className="d-flex align-items-center mb-1">
                    <div className="flex-grow-1 me-2">
                      <Field
                        name={`contacts[${index}]`} 
                        type="email"
                        component={InputText}
                        label={`Email #${index + 1}`}
                        placeholder="me@domail.tld"
                      />
                    </div>
                    <button
                      type="button"
                      className="btn btn-icon btn-light-danger"
                      onClick={() => remove(index)}
                    >
                      <KTIcon iconName="trash" className="fs-2" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </FieldArray>

          <Field
            component={FormActions}
            close={close}
          />
        </Form>
      )}
    </Formik>
  )
}

export {SupplierForm}
